import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check, ChevronDown } from "lucide-react";
import { LANGS } from "@/i18n";
import { http } from "@/lib/api";

export function LanguageSelector({ variant = "dark", persist = false }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = LANGS.find((l) => i18n.language?.startsWith(l.code)) || LANGS[0];

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("touchstart", onClick);
    return () => { document.removeEventListener("mousedown", onClick); document.removeEventListener("touchstart", onClick); };
  }, []);

  const choose = async (code) => {
    await i18n.changeLanguage(code);
    try { localStorage.setItem("nm_locale", code); } catch {}
    if (persist) { try { await http.put("/auth/locale", { locale: code }); } catch {} }
    setOpen(false);
  };

  const dark = variant === "dark";
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid="language-selector"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium border transition-colors ${
          dark
            ? "text-white/90 border-white/20 hover:bg-white/10"
            : "text-slate-700 border-slate-200 bg-white hover:bg-slate-50"
        }`}
      >
        <Globe className="w-4 h-4" />
        <span className="hidden sm:inline">{current.flag} {current.label}</span>
        <span className="sm:hidden">{current.flag}</span>
        <ChevronDown className="w-3.5 h-3.5 opacity-70" />
      </button>
      {open && (
        <div
          data-testid="language-menu"
          className="absolute left-0 sm:left-auto sm:right-0 mt-2 w-60 max-w-[calc(100vw-2rem)] max-h-[60vh] overflow-y-auto overscroll-contain bg-[#0F172A] text-slate-100 rounded-xl shadow-2xl ring-1 ring-white/10 py-1 z-[300]"
        >
          {LANGS.map((l) => {
            const active = current.code === l.code;
            return (
              <button
                type="button"
                key={l.code}
                data-testid={`lang-option-${l.code}`}
                onClick={() => choose(l.code)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left transition-colors ${
                  active ? "bg-sky-500/20 text-sky-300 font-semibold" : "text-slate-100 hover:bg-white/10"
                }`}
              >
                <span className="text-lg leading-none">{l.flag}</span>
                <span className="flex-1">{l.label}</span>
                {active && <Check className="w-4 h-4 text-sky-400 shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default LanguageSelector;
